import { TickCalendar } from "./TickCalendar";
import { SummaryBanner } from "./SummaryBanner";
import { ChoroplethToggle } from "./ChoroplethToggle";
import { useUIStore, pushUrl } from "../../store/uiStore";
import { useTicks } from "../../hooks/useTicks";
import { headerPalette } from "../../lib/headerTheme";
import { useIsMobile } from "../../lib/useIsMobile";

export function AppHeader() {
  const theme = useUIStore((s) => s.theme);
  const palette = headerPalette(theme);
  const isMobile = useIsMobile();

  return (
    <header
      style={{
        height: 52,
        background: palette.bg,
        borderBottom: `1px solid ${palette.border}`,
        display: "flex",
        alignItems: "center",
        padding: isMobile ? "0 8px" : "0 16px",
        gap: isMobile ? 8 : 20,
        flexShrink: 0,
        zIndex: 1000,
        overflowX: "auto",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
        <span style={{ fontSize: 18 }}>🔥</span>
        {!isMobile && (
          <span
            style={{
              fontWeight: 800,
              fontSize: 15,
              color: palette.logo,
              letterSpacing: "0.02em",
              whiteSpace: "nowrap",
            }}
          >
            FireFair
          </span>
        )}
      </div>

      {!isMobile && <Divider color={palette.border} />}

      <TickCalendarConnected />

      {!isMobile && <Divider color={palette.border} />}

      <ChoroplethToggle />

      <DrillResetButton />

      {!isMobile && (
        <>
          <Divider color={palette.border} />
          <SummaryBanner />
        </>
      )}

      <div style={{ marginLeft: "auto", flexShrink: 0 }}>
        <ThemeToggle />
      </div>
    </header>
  );
}

function Divider({ color }: { color: string }) {
  return <div style={{ width: 1, height: 28, background: color, flexShrink: 0 }} />;
}

// Glue the pure TickCalendar component to the global tick store + URL sync.
// Kept inline rather than promoted to its own file because there's only one
// caller and the wrapper is trivial.
function TickCalendarConnected() {
  const { activeTick, setTick } = useUIStore();
  const theme = useUIStore((s) => s.theme);
  const palette = headerPalette(theme);
  const { ticks, isLoading } = useTicks();

  if (isLoading) {
    return <span style={{ fontSize: 13, color: palette.statLabel }}>Loading ticks…</span>;
  }

  const handleSelect = (tick: string) => {
    setTick(tick);
    pushUrl({ activeTick: tick });
  };

  return <TickCalendar ticks={ticks} activeTick={activeTick} onSelect={handleSelect} />;
}

function DrillResetButton() {
  const theme = useUIStore((s) => s.theme);
  const palette = headerPalette(theme);
  const { drillCounty, setDrillCounty } = useUIStore();
  if (!drillCounty) return null;
  return (
    <button
      type="button"
      onClick={() => {
        setDrillCounty(null);
        pushUrl({ drillCounty: null });
      }}
      style={{
        padding: "4px 10px",
        fontSize: 12,
        background: palette.buttonBg,
        color: palette.logo,
        border: `1px solid ${palette.buttonBorder}`,
        borderRadius: 4,
        cursor: "pointer",
      }}
      title="Return to statewide county view"
    >
      ← Counties
    </button>
  );
}

function ThemeToggle() {
  const theme = useUIStore((s) => s.theme);
  const toggleTheme = useUIStore((s) => s.toggleTheme);
  const palette = headerPalette(theme);
  const next = theme === "dark" ? "light" : "dark";
  const label = next === "light" ? "Switch to light mode" : "Switch to dark mode";
  return (
    <button
      type="button"
      onClick={toggleTheme}
      aria-label={label}
      title={label}
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: 32,
        height: 32,
        padding: 0,
        background: palette.buttonBg,
        color: palette.icon,
        border: `1px solid ${palette.buttonBorder}`,
        borderRadius: 6,
        cursor: "pointer",
      }}
    >
      {theme === "dark" ? <SunIcon /> : <MoonIcon />}
    </button>
  );
}

function SunIcon() {
  return (
    <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
    </svg>
  );
}

function MoonIcon() {
  return (
    <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
    </svg>
  );
}
